import { useBackgroundSoundListControllerListQuery } from '@shared/services/apiService/apiService';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import {
  AmbienceSettingsDraft,
  cloneAmbienceDraft,
  createEmptyAmbienceDraft,
  getThinkingProbabilityTotal,
  hasMissingSounds,
  mapBackgroundSoundsToDraft,
  stripAmbienceDraft,
} from '../utils/ambienceSettings.utils';
import {
  AgentBackgroundSoundsResponseDto,
  BackgroundSoundResponseDto,
  CampaignBackgroundSoundsResponseDto,
} from '@shared/services/apiService/apiService';

type CampaignAmbienceSettingsControllerOptions = {
  mode: 'campaign';
  backgroundSounds: CampaignBackgroundSoundsResponseDto | null | undefined;
  isReadOnly: boolean;
  enabled?: boolean;
};

type AgentAmbienceSettingsControllerOptions = {
  mode: 'agent';
  backgroundSounds: AgentBackgroundSoundsResponseDto | null | undefined;
  campaignBackgroundSounds?: CampaignBackgroundSoundsResponseDto | null;
  isReadOnly: boolean;
  enabled?: boolean;
};

type AmbienceSettingsControllerOptions =
  | CampaignAmbienceSettingsControllerOptions
  | AgentAmbienceSettingsControllerOptions;

type AmbienceSettingsControllerResult = {
  sounds: BackgroundSoundResponseDto[];
  isLoadingSounds: boolean;
  draft: AmbienceSettingsDraft | null;
  setDraft: Dispatch<SetStateAction<AmbienceSettingsDraft | null>>;
  resetDraft: () => void;
  isDirty: boolean;
  hasValidationError: boolean;
  hasMissingSounds: boolean;
  ambientDisabled: boolean;
  thinkingDisabled: boolean;
  ambientNotice: string | null;
  thinkingNotice: string | null;
};

const cloneDraftOrNull = (draft: AmbienceSettingsDraft | null) =>
  draft ? cloneAmbienceDraft(draft) : null;

export const useAmbienceSettingsController = (
  options: AmbienceSettingsControllerOptions,
): AmbienceSettingsControllerResult => {
  const { mode, backgroundSounds, isReadOnly, enabled = true } = options;
  const { t } = useTranslation();
  const { data: sounds = [], isLoading: isLoadingSounds } =
    useBackgroundSoundListControllerListQuery(undefined, {
      skip: !enabled,
      refetchOnMountOrArgChange: true,
    });

  const [localDraft, setLocalDraft] = useState<AmbienceSettingsDraft | null>(null);
  const [serverDraft, setServerDraft] = useState<AmbienceSettingsDraft | null>(null);
  const [initializedFromServer, setInitializedFromServer] = useState(false);

  const isDirty = useMemo(() => {
    if (!localDraft || !serverDraft) {
      return false;
    }

    return !isEqual(stripAmbienceDraft(localDraft), stripAmbienceDraft(serverDraft));
  }, [localDraft, serverDraft]);

  const hasValidationError = useMemo(
    () => (localDraft ? getThinkingProbabilityTotal(localDraft) > 100 : false),
    [localDraft],
  );
  const hasMissingSoundsError = useMemo(
    () => (localDraft ? hasMissingSounds(localDraft) : false),
    [localDraft],
  );

  const nextMappedDraft = useMemo(() => {
    if (mode === 'campaign') {
      return mapBackgroundSoundsToDraft(backgroundSounds, sounds, {
        overrideAmbientSound: Boolean(backgroundSounds?.overrideAmbientSound),
        overrideThinkingSounds: Boolean(backgroundSounds?.overrideThinkingSounds),
      });
    }

    return mapBackgroundSoundsToDraft(backgroundSounds, sounds, {
      overrideAmbientSound: false,
      overrideThinkingSounds: false,
    });
  }, [backgroundSounds, mode, sounds]);

  useEffect(() => {
    if (enabled) {
      return;
    }

    setLocalDraft(null);
    setServerDraft(null);
    setInitializedFromServer(false);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (isLoadingSounds) {
      setLocalDraft((currentDraft) => currentDraft ?? createEmptyAmbienceDraft());
      return;
    }

    const nextDraft = cloneAmbienceDraft(nextMappedDraft);
    setServerDraft(nextDraft);

    if (!initializedFromServer) {
      setLocalDraft(cloneAmbienceDraft(nextMappedDraft));
      setInitializedFromServer(true);
      return;
    }

    if (isDirty) {
      return;
    }

    setLocalDraft(cloneAmbienceDraft(nextMappedDraft));
  }, [enabled, initializedFromServer, isDirty, isLoadingSounds, nextMappedDraft]);

  const resetDraft = useCallback(() => {
    setLocalDraft(cloneDraftOrNull(serverDraft) ?? createEmptyAmbienceDraft());
  }, [serverDraft]);

  const isAmbientOverrideEnabled =
    mode === 'agent' ? Boolean(options.campaignBackgroundSounds?.overrideAmbientSound) : false;
  const isThinkingOverrideEnabled =
    mode === 'agent' ? Boolean(options.campaignBackgroundSounds?.overrideThinkingSounds) : false;

  return {
    sounds,
    isLoadingSounds,
    draft: localDraft,
    setDraft: setLocalDraft,
    resetDraft,
    isDirty,
    hasValidationError,
    hasMissingSounds: hasMissingSoundsError,
    ambientDisabled: isReadOnly || isAmbientOverrideEnabled,
    thinkingDisabled: isReadOnly || isThinkingOverrideEnabled,
    ambientNotice: isAmbientOverrideEnabled
      ? t('adminMainPage.agentAmbienceSettings.ambientOverrideNotice')
      : null,
    thinkingNotice: isThinkingOverrideEnabled
      ? t('adminMainPage.agentAmbienceSettings.thinkingOverrideNotice')
      : null,
  };
};
